import React, { memo } from 'react';
import { useWindowDimensions } from 'react-native';
import Animated, {
    Extrapolation,
    SharedValue,
    clamp,
    interpolate,
    interpolateColor,
    useAnimatedStyle,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { ModalPullDownIndicator } from '@/components/PullDownIndicator';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { StickyWrapper } from '@/feature/lobby/hooks/use-sticky-translation';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { useProductSwitchModal } from '@/hooks/use-product-switch';
import { common, designSystem } from '@/styles/styles';
import { isWeb } from '@/utils/constants-platform-specific';

import { LOBBY_HANDLE_HEIGHT, productSwitcherStyles, useLobbyListStyles } from './LobbyWrapper';
import { PRODUCT_CHIPS_HEIGHT, ProductSwitcherChips } from './ProductSwitcherChips';

type LobbyProductSwitcherChipsHandleProps = {
    handleStickPosition: SharedValue<number>;
    scrollOffset: SharedValue<number>;
    scrollDirection: SharedValue<'up' | 'down'>;
    scrollVelocity: SharedValue<number>;
};

/**
 * Renders the product switcher chips and handle within the lobby.
 *
 * This component manages scroll-based animations for the handle and chips,
 * including translation, scaling, and opacity.
 *
 * Note: While the entire container is sticky, the chips are animated relative
 * to the scroll offset to create the illusion of remaining "static" at the top.
 */
export const LobbyProductSwitcherChipsHandle = memo(
    ({ handleStickPosition, scrollOffset, scrollDirection, scrollVelocity }: LobbyProductSwitcherChipsHandleProps) => {
        const insets = useSafeAreaInsets();

        const { lobbyHandleHeight } = useLobbyListStyles();

        const { height } = useWindowDimensions();

        const productSwitcherChipsStyle = useAnimatedStyle(() => {
            if (isWeb) {
                return {};
            }
            return {
                transform: [
                    {
                        translateY: clamp(scrollOffset.value, -Infinity, PRODUCT_CHIPS_HEIGHT + lobbyHandleHeight),
                    },
                ],
                opacity: interpolate(scrollOffset.value, [0, PRODUCT_CHIPS_HEIGHT], [1, 0], Extrapolation.CLAMP),
            };
        }, [scrollOffset]);

        const animatedBackgroundStyle = useAnimatedStyle(() => {
            if (isWeb) {
                return { backgroundColor: 'transparent' };
            }
            if (scrollOffset.value < 0) {
                return { backgroundColor: 'transparent' };
            }
            return {
                backgroundColor: interpolateColor(
                    scrollOffset.value,
                    [PRODUCT_CHIPS_HEIGHT, PRODUCT_CHIPS_HEIGHT + 10],
                    ['transparent', ds.colorValue.background.neutral.base]
                ),
            };
        }, [insets.top]);

        const borderRadiusStyle = useAnimatedStyle(() => {
            if (isWeb) {
                return productSwitcherStyles.borderRadius;
            }
            return {
                borderTopLeftRadius: interpolate(
                    scrollOffset.value,
                    [0, PRODUCT_CHIPS_HEIGHT + 10],
                    [32, 0],
                    Extrapolation.CLAMP
                ),
                borderTopRightRadius: interpolate(
                    scrollOffset.value,
                    [0, PRODUCT_CHIPS_HEIGHT + 10],
                    [32, 0],
                    Extrapolation.CLAMP
                ),
            };
        });

        const handleOpacityStyle = useAnimatedStyle(() => {
            return {
                opacity: interpolate(scrollOffset.value, [0, PRODUCT_CHIPS_HEIGHT], [1, 0]),
            };
        });

        const handleShadowStyle = useAnimatedStyle(() => {
            return {
                shadowOpacity: interpolate(
                    scrollOffset.value,
                    [0, PRODUCT_CHIPS_HEIGHT],
                    [0.25, 0],
                    Extrapolation.CLAMP
                ),
            };
        }, [scrollOffset]);

        const { switchingAvailable } = useProductSwitchModal();

        const scaleAnimation = useAnimatedStyle(() => {
            if (isWeb) {
                return {};
            }
            return {
                transform: [
                    {
                        scale: interpolate(
                            scrollOffset.value,
                            [0, PRODUCT_CHIPS_HEIGHT],
                            [1, 0.9],
                            Extrapolation.CLAMP
                        ),
                    },
                ],
            };
        });

        // If switching is not available, we return an empty box to avoid rendering the handle
        // and to ensure the layout remains consistent
        if (!switchingAvailable) {
            return <Box height={insets.top} width="100%" />;
        }

        return (
            <StickyWrapper
                initialPosition={handleStickPosition}
                scrollOffset={scrollOffset}
                scrollDirection={scrollDirection}
                scrollVelocity={scrollVelocity}
            >
                <Box
                    height={insets.top + lobbyHandleHeight + PRODUCT_CHIPS_HEIGHT}
                    backgroundColor={ds.colorToken.background.brand.strong}
                    overflow={'hidden'}
                    style={{ paddingTop: insets.top }}
                >
                    <Animated.View style={[productSwitcherChipsStyle]}>
                        <MaxWidthWrapper>
                            <ProductSwitcherChips scaleAnimation={scaleAnimation} />
                        </MaxWidthWrapper>
                    </Animated.View>
                    <Box
                        position={'absolute'}
                        height={insets.top + lobbyHandleHeight + PRODUCT_CHIPS_HEIGHT}
                        width="100%"
                        pointerEvents="box-none"
                        overflow={'hidden'}
                    >
                        <Animated.View style={[common.flex, animatedBackgroundStyle]} pointerEvents={'box-none'}>
                            <Box
                                top={PRODUCT_CHIPS_HEIGHT + insets.top}
                                width={'100%'}
                                zIndex={1}
                                pointerEvents="box-none"
                            >
                                <Animated.View
                                    style={[productSwitcherStyles.lobbySheetShadow, { height }, handleShadowStyle]}
                                >
                                    <Animated.View
                                        style={[
                                            productSwitcherStyles.lobbyHandle,
                                            {
                                                height: LOBBY_HANDLE_HEIGHT,
                                                paddingBottom: height,
                                            },
                                            borderRadiusStyle,
                                        ]}
                                    >
                                        <Animated.View style={handleOpacityStyle}>
                                            <Box top={12}>
                                                <ModalPullDownIndicator color={designSystem.colors.neutral500} />
                                            </Box>
                                        </Animated.View>
                                    </Animated.View>
                                </Animated.View>
                            </Box>
                        </Animated.View>
                    </Box>
                </Box>
            </StickyWrapper>
        );
    }
);

LobbyProductSwitcherChipsHandle.displayName = 'LobbyProductSwitcherChipsHandle';
